import React, { useState } from 'react'
import {
    Typography,
    Paper,
    CircularProgress,
    Button,
    ButtonGroup,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { Container } from '../../Container'
import {
    AnalyticsSummary,
    DrillStep,
    pivotToHeatmapSeries,
    formatLabel,
} from './analysisTypes'
import { HeatmapDialog } from './HeatmapDialog'

const BAR_LABEL_STYLE = {
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
} as const

interface Props {
    summary: AnalyticsSummary | null
    loading: boolean
    drillInto: (
        dimension: 'topic' | 'intent',
        value: string,
        label: string,
    ) => void
    drillPath: DrillStep[]
    groupByTime: 'month' | 'week'
    onGroupByTimeChange: (value: 'month' | 'week') => void
}

const KPICard = ({
    title,
    value,
    subtitle,
}: {
    title: string
    value: string | number
    subtitle?: string
}) => {
    const theme = useTheme()
    return (
        <Paper
            style={{
                flex: 1,
                padding: theme.spacing(2),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 150,
            }}
            elevation={1}
        >
            <Typography variant="caption" color="textSecondary">
                {title}
            </Typography>
            <Typography variant="h4">{value}</Typography>
            {subtitle && (
                <Typography variant="caption" color="textSecondary">
                    {subtitle}
                </Typography>
            )}
        </Paper>
    )
}

export const AnalysisDashboard = ({
    summary,
    loading,
    drillInto,
    drillPath,
    groupByTime,
    onGroupByTimeChange,
}: Props) => {
    const theme = useTheme()
    const [heatmapOpen, setHeatmapOpen] = useState(false)

    if (loading || !summary) {
        return (
            <Container
                style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 400,
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    const hasDrilledTopic = drillPath.some((s) => s.dimension === 'topic')
    const hasDrilledIntent = drillPath.some((s) => s.dimension === 'intent')

    const topicChartHeight = Math.max(200, summary.by_topic.length * 35)
    const intentChartHeight = Math.max(200, summary.by_intent.length * 35)

    const topicChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            events: {
                dataPointSelection: (
                    _event: unknown,
                    _chartContext: unknown,
                    config: { dataPointIndex: number },
                ) => {
                    const item = summary.by_topic[config.dataPointIndex]
                    if (item) {
                        drillInto('topic', String(item.topic_id), item.label)
                    }
                },
            },
            toolbar: { show: false },
        },
        plotOptions: {
            bar: { horizontal: true, barHeight: '60%' },
        },
        dataLabels: { enabled: true },
        xaxis: {
            categories: summary.by_topic.map((t) => formatLabel(t.label)),
        },
        yaxis: {
            labels: { style: BAR_LABEL_STYLE },
        },
        tooltip: { enabled: true },
        colors: [theme.palette.primary.main],
    }

    const topicChartSeries = [
        {
            name: 'Conversations',
            data: summary.by_topic.map((t) => t.count),
        },
    ]

    const intentChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            events: {
                dataPointSelection: (
                    _event: unknown,
                    _chartContext: unknown,
                    config: { dataPointIndex: number },
                ) => {
                    const item = summary.by_intent[config.dataPointIndex]
                    if (item) {
                        drillInto('intent', item.intent, item.label)
                    }
                },
            },
            toolbar: { show: false },
        },
        plotOptions: {
            bar: { horizontal: true, barHeight: '60%' },
        },
        dataLabels: { enabled: true },
        xaxis: {
            categories: summary.by_intent.map((i) => formatLabel(i.label)),
        },
        yaxis: {
            labels: { style: BAR_LABEL_STYLE },
        },
        tooltip: { enabled: true },
        colors: [theme.palette.secondary.main],
    }

    const intentChartSeries = [
        {
            name: 'Conversations',
            data: summary.by_intent.map((i) => i.count),
        },
    ]

    const timelineOptions: ApexOptions = {
        chart: {
            type: 'area',
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: {
            categories: summary.over_time.map((p) => p.period),
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.4,
                opacityTo: 0.1,
            },
        },
        colors: [theme.palette.primary.main],
        tooltip: { enabled: true },
    }

    const timelineSeries = [
        {
            name: 'Conversations',
            data: summary.over_time.map((p) => p.count),
        },
    ]

    const heatmapData = pivotToHeatmapSeries(summary.by_topic_and_intent)
    const heatmapChartHeight = Math.max(200, heatmapData.length * 30)
    const workspaceChartHeight = Math.max(200, summary.by_workspace.length * 30)

    const miniHeatmapOptions: ApexOptions = {
        chart: {
            type: 'heatmap',
            toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        colors: [theme.palette.primary.main],
        xaxis: {
            labels: { show: true, rotate: -45, rotateAlways: true },
        },
        tooltip: { enabled: true },
    }

    const workspaceChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            toolbar: { show: false },
        },
        plotOptions: {
            bar: { horizontal: true, barHeight: '60%' },
        },
        dataLabels: { enabled: true },
        xaxis: {
            categories: summary.by_workspace.map((w) =>
                formatLabel(w.workspace_name),
            ),
        },
        yaxis: {
            labels: { style: BAR_LABEL_STYLE },
        },
        colors: [theme.palette.info?.main ?? '#2196f3'],
        tooltip: { enabled: true },
    }

    const workspaceChartSeries = [
        {
            name: 'Conversations',
            data: summary.by_workspace.map((w) => w.count),
        },
    ]

    const classificationRate =
        summary.total > 0
            ? Math.round((summary.classified / summary.total) * 100)
            : 0

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                gap: theme.spacing(2),
                padding: theme.spacing(2),
                overflowY: 'auto',
                minWidth: 0,
                minHeight: 0,
            }}
        >
            {/* KPI Row */}
            <Container
                style={{
                    flexDirection: 'row',
                    gap: theme.spacing(2),
                }}
            >
                <KPICard title="Total Conversations" value={summary.total} />
                <KPICard
                    title="Classified"
                    value={summary.classified}
                    subtitle={`${classificationRate}% of total`}
                />
                <KPICard title="Unclassified" value={summary.unclassified} />
                <KPICard
                    title="Workspaces"
                    value={summary.by_workspace.length}
                />
            </Container>

            {/* Topic & Intent Charts */}
            <Container
                style={{
                    flexDirection: 'row',
                    gap: theme.spacing(2),
                }}
            >
                {/* Topic Breakdown */}
                <Paper
                    style={{
                        flex: 1,
                        padding: theme.spacing(2),
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    elevation={1}
                >
                    <Typography variant="h6" gutterBottom>
                        {hasDrilledTopic
                            ? `Topic: ${
                                  drillPath.find((s) => s.dimension === 'topic')
                                      ?.label
                              }`
                            : 'By Topic'}
                    </Typography>
                    {hasDrilledTopic ? (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ fontStyle: 'italic' }}
                        >
                            Currently filtered to:{' '}
                            {
                                drillPath.find((s) => s.dimension === 'topic')
                                    ?.label
                            }
                        </Typography>
                    ) : summary.by_topic.length > 0 ? (
                        <div
                            style={{
                                maxHeight: intentChartHeight + 30,
                                overflowY:
                                    topicChartHeight > intentChartHeight
                                        ? 'auto'
                                        : 'hidden',
                            }}
                        >
                            <ReactApexChart
                                options={topicChartOptions}
                                series={topicChartSeries}
                                type="bar"
                                height={topicChartHeight}
                            />
                        </div>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No topic data available
                        </Typography>
                    )}
                </Paper>

                {/* Intent Breakdown */}
                <Paper
                    style={{
                        flex: 1,
                        padding: theme.spacing(2),
                        minWidth: 0,
                    }}
                    elevation={1}
                >
                    <Typography variant="h6" gutterBottom>
                        {hasDrilledIntent
                            ? `Intent: ${
                                  drillPath.find(
                                      (s) => s.dimension === 'intent',
                                  )?.label
                              }`
                            : 'By Intent'}
                    </Typography>
                    {hasDrilledIntent ? (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ fontStyle: 'italic' }}
                        >
                            Currently filtered to:{' '}
                            {
                                drillPath.find((s) => s.dimension === 'intent')
                                    ?.label
                            }
                        </Typography>
                    ) : summary.by_intent.length > 0 ? (
                        <ReactApexChart
                            options={intentChartOptions}
                            series={intentChartSeries}
                            type="bar"
                            height={intentChartHeight}
                        />
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No intent data available
                        </Typography>
                    )}
                </Paper>
            </Container>

            {/* Timeline */}
            {summary.over_time.length > 0 && (
                <Paper style={{ padding: theme.spacing(2) }} elevation={1}>
                    <Container
                        style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        <Typography variant="h6">
                            Conversations Over Time
                        </Typography>
                        <ButtonGroup size="small" variant="outlined">
                            <Button
                                variant={
                                    groupByTime === 'week'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                onClick={() => onGroupByTimeChange('week')}
                            >
                                Week
                            </Button>
                            <Button
                                variant={
                                    groupByTime === 'month'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                onClick={() => onGroupByTimeChange('month')}
                            >
                                Month
                            </Button>
                        </ButtonGroup>
                    </Container>
                    <ReactApexChart
                        options={timelineOptions}
                        series={timelineSeries}
                        type="area"
                        height={250}
                    />
                </Paper>
            )}

            {/* Heatmap Mini + Workspace */}
            <Container
                style={{
                    flexDirection: 'row',
                    gap: theme.spacing(2),
                }}
            >
                {/* Heatmap */}
                {heatmapData.length > 0 && (
                    <Paper
                        style={{
                            flex: 2,
                            padding: theme.spacing(2),
                            minWidth: 0,
                        }}
                        elevation={1}
                    >
                        <Container
                            style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            <Typography variant="h6">Topic x Intent</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setHeatmapOpen(true)}
                            >
                                View Full Heatmap
                            </Button>
                        </Container>
                        <ReactApexChart
                            options={miniHeatmapOptions}
                            series={heatmapData}
                            type="heatmap"
                            height={heatmapChartHeight}
                        />
                    </Paper>
                )}

                {/* Workspace Breakdown */}
                {summary.by_workspace.length > 0 && (
                    <Paper
                        style={{
                            flex: 1,
                            padding: theme.spacing(2),
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        elevation={1}
                    >
                        <Typography variant="h6" gutterBottom>
                            Top Workspaces
                        </Typography>
                        <div
                            style={{
                                maxHeight: heatmapChartHeight + 30,
                                overflowY:
                                    workspaceChartHeight > heatmapChartHeight
                                        ? 'auto'
                                        : 'hidden',
                            }}
                        >
                            <ReactApexChart
                                options={workspaceChartOptions}
                                series={workspaceChartSeries}
                                type="bar"
                                height={workspaceChartHeight}
                            />
                        </div>
                    </Paper>
                )}
            </Container>

            {/* Heatmap Dialog */}
            <HeatmapDialog
                open={heatmapOpen}
                onClose={() => setHeatmapOpen(false)}
                data={summary.by_topic_and_intent}
                drillInto={drillInto}
            />
        </Container>
    )
}
